import {
  Control,
  Controller,
  FormState,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form';
import { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { SharedContactModel } from '../../../../types';
import ContactEditOrganizationDialog from './contact-edit-organization-dialog';
import ContactEditAddressDialog from './contact-edit-address-dialog';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircle from '@mui/icons-material/AddCircle';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface ContactAccordionSectionProps {
  control: Control<Partial<SharedContactModel>>;
  controlName:
    | 'emails'
    | 'phoneNumbers'
    | 'addresses'
    | 'relations'
    | 'orgs'
    | 'events'
    | 'im'
    | 'webSites';
  title: string;
  selectPropertyOne: string;
  inputProperty: string;
  menuItemsOne: { label: string; val: string }[];
  formState: FormState<Partial<SharedContactModel>>;
  setValue: UseFormSetValue<Partial<SharedContactModel>>;
  watch: UseFormWatch<Partial<SharedContactModel>>;
  getValues: UseFormGetValues<Partial<SharedContactModel>>;
  disabled: boolean;
}

export default function ContactAccordionSection({
  control,
  controlName,
  title,
  selectPropertyOne,
  menuItemsOne,
  inputProperty,
  formState,
  watch,
  setValue,
  disabled,
  getValues,
}: ContactAccordionSectionProps) {
  const [open, setOpen] = useState(false);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [openOrgsDialog, setOpenOrgsDialog] = useState(false);
  const [clickedIndex, setClickedIndex] = useState(0);
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: controlName,
  });

  // close accordion if there are no items in the accordion
  useEffect(() => {
    if (fields.length === 0) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [fields]);

  // open accordion if there is an error in the accordion
  useEffect(() => {
    if (formState.errors[controlName]) {
      setOpen(true);
    }
  }, [controlName, formState]);

  // scroll to errors in accordion
  useEffect(() => {
    if (!formState.errors) return;
    const firstErrorField = Object.keys(formState.errors)[0];
    if (firstErrorField) {
      const errorElement = document.querySelector(`[name=${firstErrorField}]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [formState]);

  return (
    <>
      <Accordion
        expanded={open}
        onChange={() => {
          if (fields.length === 0) return;
          setOpen(!open);
        }}
      >
        <AccordionSummary
          expandIcon={
            <IconButton
              disabled={fields.length === 0 || disabled}
              sx={{ mr: -1 }}
              disableRipple
            >
              <ExpandMore />
            </IconButton>
          }
          aria-controls={`${controlName}-content`}
          id={`${controlName}}-header`}
        >
          <Stack flexDirection="row" alignItems="center" width="100%" gap={1}>
            <Typography
              sx={{
                color: (theme) =>
                  formState.errors[controlName]
                    ? theme.palette.error.main
                    : 'inherit',
              }}
            >
              {title}
            </Typography>
            {
              <Tooltip title="Add" arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (controlName === 'emails') {
                        append({
                          rel: '',
                          val: '',
                        });
                      } else if (controlName === 'events') {
                        append({
                          rel: '',
                          val: '',
                        });
                      } else if (controlName === 'webSites') {
                        append({
                          rel: '',
                          val: '',
                        });
                      } else if (controlName === 'phoneNumbers') {
                        append({
                          rel: '',
                          val: '',
                        });
                      } else if (controlName === 'relations') {
                        append({
                          rel: '',
                          val: '',
                          addrVal: null,
                          orgVal: null,
                        });
                      } else if (controlName === 'im') {
                        append({
                          rel: '',
                          val: '',
                        });
                      } else if (controlName === 'addresses') {
                        append({
                          rel: '',
                          val: '',
                          addrVal: {
                            addressString: '',
                            poBox: '',
                            street: '',
                            city: '',
                            postCode: '',
                            country: '',
                            neighborhood: '',
                            state: '',
                          },
                        });
                      } else if (controlName === 'orgs') {
                        append({
                          rel: '',
                          val: '',
                          orgVal: {
                            orgString: '',
                            orgName: '',
                            orgTitle: '',
                            primary: false,
                            orgDepartment: '',
                            orgSymbol: '',
                            where: '',
                            orgJobDescription: '',
                          },
                        });
                      }
                      setOpen(true);
                    }}
                  >
                    <AddCircle />
                  </IconButton>
                </span>
              </Tooltip>
            }
            {fields.length > 0 ? (
              <Chip label={fields.length} color="primary" size="small" />
            ) : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 140, overflowY: 'auto' }}>
          <Stack
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            {fields.map((item, index) => {
              const formattedAddress = watch(
                `addresses.${index}.addrVal.addressString`
              );

              return (
                <Stack
                  gap={1}
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  key={item.id}
                >
                  <Stack flexDirection="row" alignItems="center" gap={2}>
                    <Controller
                      rules={{ required: 'This field is required.' }}
                      name={
                        `${controlName}.${index}.${selectPropertyOne}` as keyof Partial<SharedContactModel>
                      }
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          disabled={disabled}
                          inputRef={field.ref}
                          inputProps={{
                            id: `${controlName}-type-${index}`,
                          }}
                          InputLabelProps={{
                            htmlFor: `${controlName}-type-${index}`,
                          }}
                          name={controlName}
                          select
                          label={`${selectPropertyOne}`}
                          error={
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            !!formState.errors[controlName]?.[index]?.[
                              selectPropertyOne
                            ]
                          }
                          helperText={
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            formState.errors[controlName]?.[index]?.[
                              selectPropertyOne
                            ]?.message || ' '
                          }
                          sx={{
                            // width: '100%',
                            minWidth: 150,
                            // maxWidth: 500,
                            textTransform: 'capitalize',
                          }}
                        >
                          {menuItemsOne.map((item) => (
                            <MenuItem value={item.val} key={item.label}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    {controlName === 'addresses' ? (
                      <Typography
                        variant="caption"
                        sx={{
                          whiteSpace: 'pre-line',
                          mb: 3,
                        }}
                      >
                        {`${formattedAddress}`}
                      </Typography>
                    ) : controlName === 'orgs' ? (
                      <Typography
                        variant="caption"
                        sx={{
                          whiteSpace: 'pre-line',
                          mb: 3,
                        }}
                      >
                        {`${watch(`orgs.${index}.orgVal.orgName`) || ''}`}
                      </Typography>
                    ) : controlName === 'events' ? (
                      <Controller
                        rules={{ required: 'This field is required.' }}
                        name={
                          `${controlName}.${index}.${inputProperty}` as keyof Partial<SharedContactModel>
                        }
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <DatePicker
                            label="Date"
                            sx={{
                              minWidth: 250,
                            }}
                            disabled={disabled}
                            value={value ? dayjs(value.toString()) : null}
                            onChange={(e) => {
                              if (e) {
                                onChange(dayjs(e).format('MM-DD-YYYY'));
                              }
                            }}
                            slotProps={{
                              textField: {
                                error:
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  // @ts-ignore
                                  !!formState.errors[controlName]?.[index]?.[
                                    inputProperty
                                  ],
                                helperText:
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  // @ts-ignore
                                  formState.errors[controlName]?.[index]?.[
                                    inputProperty
                                  ]?.message || ' ',
                              },
                            }}
                          />
                        )}
                      />
                    ) : (
                      <Controller
                        rules={{ required: 'This field is required.' }}
                        name={
                          `${controlName}.${index}.${inputProperty}` as keyof Partial<SharedContactModel>
                        }
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required
                            disabled={disabled}
                            inputRef={field.ref}
                            error={
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              !!formState.errors[controlName]?.[index]?.[
                                inputProperty
                              ]
                            }
                            helperText={
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              formState.errors[controlName]?.[index]?.[
                                inputProperty
                              ]?.message || ' '
                            }
                            autoComplete="off"
                            label={`${inputProperty}`}
                            sx={{
                              minWidth: 250,
                              textTransform: 'capitalize',
                            }}
                          />
                        )}
                      />
                    )}

                    {controlName === 'orgs' || controlName === 'addresses' ? (
                      <Tooltip
                        title={`Edit ${
                          controlName === 'orgs' ? 'Organization' : 'Address'
                        }`}
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            sx={{ mb: 3 }}
                            disabled={disabled}
                            onClick={() => {
                              setClickedIndex(index);

                              if (controlName === 'orgs') {
                                setOpenOrgsDialog(true);
                              } else {
                                setOpenAddressDialog(true);
                              }
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                  </Stack>
                  <Stack flexDirection="row" gap={2} sx={{ mb: 3 }}>
                    <Tooltip title="Move Down" arrow>
                      <span>
                        <IconButton
                          disabled={index === fields.length - 1 || disabled}
                          size="small"
                          onClick={() => {
                            move(index, index + 1);
                          }}
                        >
                          <ArrowDownward />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move Up" arrow>
                      <span>
                        <IconButton
                          disabled={index === 0 || disabled}
                          size="small"
                          onClick={() => {
                            move(index, index - 1);
                          }}
                        >
                          <ArrowUpward />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={
                            disabled ||
                            (controlName === 'emails' && fields.length === 1)
                          }
                          onClick={() => {
                            remove(index);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>
      {openAddressDialog ? (
        <ContactEditAddressDialog
          openAddressDialog={openAddressDialog}
          setOpenAddressDialog={setOpenAddressDialog}
          clickedIndex={clickedIndex}
          setValue={setValue}
          disabled={disabled}
          getValues={getValues}
        />
      ) : null}
      {openOrgsDialog ? (
        <ContactEditOrganizationDialog
          openOrganizationDialog={openOrgsDialog}
          setOpenOrganizationDialog={setOpenOrgsDialog}
          clickedIndex={clickedIndex}
          setValue={setValue}
          disabled={disabled}
          getValues={getValues}
        />
      ) : null}
    </>
  );
}
